"use client";

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";

export function GoogleSignIn({ linking = false }: { linking?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const status = useQuery({
    queryKey: ["google-auth-status"],
    queryFn: async () => {
      const response = await fetch("/api/auth/google-status", {
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Google sign-in unavailable");
      return {
        ...((await response.json()) as { enabled: boolean }),
        failed:
          new URLSearchParams(window.location.search).has("error") ||
          new URLSearchParams(window.location.search).get("google") === "error",
      };
    },
    retry: false,
  });
  if (!status.data?.enabled)
    return linking ? (
      <p>
        Google connection is not enabled yet. Your password login still works.
        Use this deployment’s HTTPS address when it is enabled.
      </p>
    ) : null;
  async function connect() {
    setBusy(true);
    setError("");
    try {
      const result = linking
        ? await authClient.linkSocial({
            provider: "google",
            callbackURL: "/account?google=connected",
            errorCallbackURL: "/account?google=error",
          })
        : await authClient.signIn.social({
            provider: "google",
            callbackURL: "/",
            errorCallbackURL: "/sign-in?google=error",
          });
      if (result.error) throw new Error("Google sign-in could not start");
    } catch {
      setError(
        "Could not connect to Google. Your password login remains available."
      );
      setBusy(false);
    }
  }
  return (
    <div className="mt-5 space-y-3">
      {status.data.failed && (
        <p role="alert">
          Google sign-in was not completed. Use your password, then connect the
          matching Google account from Account, or ask your administrator.
        </p>
      )}
      <Button
        type="button"
        variant="outline"
        className="min-h-12 w-full"
        disabled={busy}
        onClick={connect}
      >
        {busy
          ? "Opening Google…"
          : linking
            ? "Connect Google"
            : "Continue with Google"}
      </Button>
      {!linking && (
        <p className="text-muted-foreground text-sm">
          First time? Sign in with your password, then open Account → Connect
          Google.
        </p>
      )}
      {error && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
